import { useCallback, useEffect, useRef } from 'react';
import { Image, LayoutRectangle } from 'react-native';
import { withTiming } from 'react-native-reanimated';
import {
  CLOSE_TIMING,
  DESKTOP_IMAGE_SCALE,
  OPEN_TIMING,
} from './ImageViewerConstants';
import { handleWebHistory, resetPaneTransforms } from './ImageViewerHelpers';
import { getCachedDimensions, setCachedDimensions } from './imageDimensionsCache';
import type { IImageViewerSharedValues } from './useImageViewerSharedValues';
import type { IPaneSlots } from './usePaneSlots';

interface UseImageViewerAnimationProps {
  visible: boolean;
  uri: string | null;
  thumbnailLayout?: LayoutRectangle | null;
  isDesktop: boolean;
  isLargeScreen: boolean;
  isBottomSheetOpen: boolean;
  onClose: () => void;
  // Fired synchronously when a close begins (before the close animation runs), so the
  // caller can hide the chrome up front. Without this the chrome only hides when the
  // close completes, leaving its dark blur sampling the fading backdrop on Android.
  onCloseStart?: () => void;
  resetState: () => void;
  isClosingProgrammatically: { current: boolean };
  sv: IImageViewerSharedValues;
  paneSlots: IPaneSlots;
  // Carousel state needed to keep trackTranslateX synced on rotation. The
  // active index drives the resting track position (-activeIndex * paneWidth).
  activeIndex: number;
  paneWidth: number;
  // Measured viewer container height. Used to vertically center the morph
  // overlay so it lines up with the carousel and the side prev/next arrows
  // (all of which live in the same parent View). Falls back to paneWidth-paired
  // window height if measurement isn't available yet on the first frame.
  paneHeight: number;
}

export interface ImageRect {
  targetX: number;
  targetY: number;
  targetWidth: number;
  targetHeight: number;
}

export const useImageViewerAnimation = ({
  visible,
  uri,
  thumbnailLayout,
  isDesktop,
  isLargeScreen,
  isBottomSheetOpen,
  onClose,
  onCloseStart,
  resetState,
  isClosingProgrammatically,
  sv,
  paneSlots,
  activeIndex,
  paneWidth,
  paneHeight,
}: UseImageViewerAnimationProps) => {
  const hasAnimatedOpenRef = useRef(false);
  const lastAnimatedUriRef = useRef<string | null>(null);
  const prevScreenRef = useRef({ width: paneWidth, height: paneHeight });
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handoffTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimeoutRef = useCallback((ref: { current: ReturnType<typeof setTimeout> | null }) => {
    if (ref.current !== null) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimeoutRef(closeTimeoutRef);
      clearTimeoutRef(handoffTimeoutRef);
    };
  }, [clearTimeoutRef]);

  const calculateImagePosition = useCallback((
    imgWidth: number,
    imgHeight: number
  ): ImageRect => {
    const containerRatio = paneWidth / paneHeight;
    const imgRatio = imgWidth / imgHeight;
    let targetWidth, targetHeight;

    if (imgRatio > containerRatio) {
      targetWidth = paneWidth;
      targetHeight = paneWidth / imgRatio;
    } else {
      targetHeight = paneHeight;
      targetWidth = paneHeight * imgRatio;
    }

    if (isDesktop) {
      targetWidth *= DESKTOP_IMAGE_SCALE;
      targetHeight *= DESKTOP_IMAGE_SCALE;
    }

    const targetX = (paneWidth - targetWidth) / 2;
    // Center within the measured viewer container — same parent as the side
    // prev/next arrows, so morph + carousel + arrows all share the exact same
    // centerline regardless of how Android reports window dimensions.
    const targetY = (paneHeight - targetHeight) / 2;

    return { targetX, targetY, targetWidth, targetHeight };
  }, [paneWidth, paneHeight, isDesktop]);

  const calculateFallbackPosition = useCallback((): ImageRect => {
    let width = paneWidth;
    let height = paneHeight;

    if (isDesktop) {
      width *= DESKTOP_IMAGE_SCALE;
      height *= DESKTOP_IMAGE_SCALE;
    }

    const targetX = (paneWidth - width) / 2;
    const targetY = (paneHeight - height) / 2;

    return { targetX, targetY, targetWidth: width, targetHeight: height };
  }, [paneWidth, paneHeight, isDesktop]);

  // Resolve the on-screen target rect for a URI. Synchronous when dimensions
  // are already cached; otherwise falls back to a one-shot Image.getSize.
  const measurePosition = useCallback((
    u: string,
    onResolved: (p: ImageRect) => void
  ): void => {
    const cached = getCachedDimensions(u);
    if (cached) {
      onResolved(calculateImagePosition(cached.w, cached.h));
      return;
    }
    Image.getSize(
      u,
      (imgWidth, imgHeight) => {
        setCachedDimensions(u, imgWidth, imgHeight);
        onResolved(calculateImagePosition(imgWidth, imgHeight));
      },
      () => {
        onResolved(calculateFallbackPosition());
      }
    );
  }, [calculateImagePosition, calculateFallbackPosition]);

  // Synchronously update the morph rect SVs to reflect a given URI's natural
  // rect. Used so the (unmounted but ready-to-mount) morph overlay and the
  // worklet tap-check both have the correct geometry for the active image.
  const syncMorphRectForUri = useCallback((u: string) => {
    measurePosition(u, ({ targetX, targetY, targetWidth, targetHeight }) => {
      sv.animLeft.value = targetX;
      sv.animTop.value = targetY;
      sv.animWidth.value = targetWidth;
      sv.animHeight.value = targetHeight;
    });
  }, [measurePosition, sv]);

  // Close the viewer with an animated morph back to the thumbnail rect.
  const closeViewer = useCallback(() => {
    handleWebHistory(visible, isClosingProgrammatically);
    onCloseStart?.();
    clearTimeoutRef(closeTimeoutRef);
    clearTimeoutRef(handoffTimeoutRef);

    if (thumbnailLayout && uri) {
      // Hand off from carousel → morph overlay before animating to the thumbnail.
      // The morph is already mounted (we keep it mounted across the open handoff)
      // and its rect already matches the active image's natural rect (kept in
      // sync by syncMorphRectForUri after each commit). Both SV writes below
      // land in the same UI-thread tick, so the swap is gap-free — no flash.
      sv.animOpacity.value = 1;
      sv.carouselOpacity.value = 0;

      sv.animLeft.value = withTiming(thumbnailLayout.x, CLOSE_TIMING);
      sv.animTop.value = withTiming(thumbnailLayout.y, CLOSE_TIMING);
      sv.animWidth.value = withTiming(thumbnailLayout.width, CLOSE_TIMING);
      sv.animHeight.value = withTiming(thumbnailLayout.height, CLOSE_TIMING);
      sv.animOpacity.value = withTiming(0, { duration: 200 });
      sv.bgOpacity.value = withTiming(0, { duration: 200 });

      closeTimeoutRef.current = setTimeout(() => {
        closeTimeoutRef.current = null;
        onClose();
        resetState();
      }, CLOSE_TIMING.duration);
    } else {
      onClose();
      resetState();
    }
  }, [visible, thumbnailLayout, uri, onClose, onCloseStart, resetState, isClosingProgrammatically, clearTimeoutRef, sv]);

  // Close instantly without morphing back to the thumbnail (e.g. swipe-down).
  const closeViewerInstant = useCallback(() => {
    handleWebHistory(visible, isClosingProgrammatically);
    onCloseStart?.();
    clearTimeoutRef(closeTimeoutRef);
    clearTimeoutRef(handoffTimeoutRef);

    sv.bgOpacity.value = withTiming(0, { duration: 100 });
    sv.carouselOpacity.value = withTiming(0, { duration: 100 });
    sv.animOpacity.value = withTiming(0, { duration: 100 });

    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      onClose();
      resetState();
    }, 100);
  }, [visible, onClose, onCloseStart, resetState, isClosingProgrammatically, clearTimeoutRef, sv]);

  // Open the viewer with the morph animation, then hand off to the carousel.
  useEffect(() => {
    if (!visible) {
      hasAnimatedOpenRef.current = false;
      lastAnimatedUriRef.current = null;
      // Clear a pending open-handoff so it can't fire after a route-change close
      // (which bypasses closeViewer) and corrupt the carousel/morph SVs for a
      // fast re-open.
      clearTimeoutRef(handoffTimeoutRef);
      return;
    }

    if (visible && uri && !hasAnimatedOpenRef.current) {
      // Reset transforms on all panes so a previously zoomed pane doesn't
      // resurrect old state on re-open.
      resetPaneTransforms(paneSlots.slots[0]);
      resetPaneTransforms(paneSlots.slots[1]);
      resetPaneTransforms(paneSlots.slots[2]);
      sv.trackTranslateY.value = 0;

      if (thumbnailLayout) {
        // Carousel sits behind the morph, hidden until handoff.
        sv.carouselOpacity.value = 0;

        // Reveal the morph (from the thumbnail rect) and the blurred background
        // IMMEDIATELY — do NOT gate this on measurePosition. On a cold open the
        // measure falls back to Image.getSize(uri), and RN's getSize bypasses
        // expo-image's cache and downloads the full image just to read its size,
        // so on a slow connection it resolves only once the full photo has
        // arrived. Gating the open animation on it left the viewer black (no
        // background, no morph) until the photo loaded. Start from the thumbnail
        // rect now and refine to the natural rect once the size is known (below).
        sv.animLeft.value = thumbnailLayout.x;
        sv.animTop.value = thumbnailLayout.y;
        sv.animWidth.value = thumbnailLayout.width;
        sv.animHeight.value = thumbnailLayout.height;
        sv.animOpacity.value = 0;
        sv.bgOpacity.value = 0;
        sv.animOpacity.value = withTiming(1, { duration: 200 });
        sv.bgOpacity.value = withTiming(1, OPEN_TIMING);

        // Hand off to the carousel as the morph completes, independent of the
        // (possibly slow) measure. The morph and the carousel render the same
        // image at the same natural rect, so this is a visually invisible
        // snap-swap. We keep the morph mounted (just invisible via
        // animOpacity=0) so the close transition can re-show it instantly
        // without a mount-induced flash.
        handoffTimeoutRef.current = setTimeout(() => {
          handoffTimeoutRef.current = null;
          sv.carouselOpacity.value = 1;
          sv.animOpacity.value = 0;
        }, OPEN_TIMING.duration);

        // Animate the morph rect from the thumbnail to the image's natural rect
        // once the dimensions are known (synchronous when already cached).
        measurePosition(uri, ({ targetX, targetY, targetWidth, targetHeight }) => {
          sv.animLeft.value = withTiming(targetX, OPEN_TIMING);
          sv.animTop.value = withTiming(targetY, OPEN_TIMING);
          sv.animWidth.value = withTiming(targetWidth, OPEN_TIMING);
          sv.animHeight.value = withTiming(targetHeight, OPEN_TIMING);
        });
      } else {
        // No thumbnail layout: snap-open straight into the carousel. Reveal the
        // background and carousel immediately — same getSize caveat as above, so
        // don't gate the reveal on the measure. Morph stays mounted but
        // invisible (animOpacity=0) for instant close later.
        sv.animOpacity.value = 0;
        sv.bgOpacity.value = withTiming(1, OPEN_TIMING);
        sv.carouselOpacity.value = withTiming(1, OPEN_TIMING);
        measurePosition(uri, ({ targetX, targetY, targetWidth, targetHeight }) => {
          sv.animLeft.value = targetX;
          sv.animTop.value = targetY;
          sv.animWidth.value = targetWidth;
          sv.animHeight.value = targetHeight;
        });
      }
      hasAnimatedOpenRef.current = true;
      lastAnimatedUriRef.current = uri;
    }
  }, [visible, uri, thumbnailLayout, measurePosition, sv, paneSlots]);

  // Keep the morph rect (used for tap-check + close-to-thumbnail) in sync with
  // the active URI as the user navigates the carousel.
  useEffect(() => {
    if (!visible || !uri || !hasAnimatedOpenRef.current) return;
    if (lastAnimatedUriRef.current === uri) return;
    lastAnimatedUriRef.current = uri;
    syncMorphRectForUri(uri);
  }, [visible, uri, syncMorphRectForUri]);

  // Reposition on rotation / container resize: re-measure for the new
  // orientation. The carousel's pane dimensions come from JS (re-rendered on
  // container layout change), so the carousel itself self-repositions; we
  // just need to refresh the morph rect.
  useEffect(() => {
    const prev = prevScreenRef.current;
    prevScreenRef.current = { width: paneWidth, height: paneHeight };

    if (!visible || !hasAnimatedOpenRef.current || !uri) return;
    if (prev.width === paneWidth && prev.height === paneHeight) return;

    syncMorphRectForUri(uri);
    // Reset any active pane drag/zoom state on rotation to avoid awkward layouts,
    // and re-sync the carousel track to the new resting position for the
    // post-rotation paneWidth.
    resetPaneTransforms(paneSlots.slots[paneSlots.activeSlot.value]);
    const rest = -activeIndex * paneWidth;
    sv.trackTranslateX.value = rest;
    sv.lastTrackTranslateX.value = rest;
  }, [paneWidth, paneHeight]);

  // Shift the carousel upward when the bottom sheet opens (mobile only).
  useEffect(() => {
    if (!visible || !hasAnimatedOpenRef.current || isLargeScreen) return;

    // Shift amount: enough to move the active image flush to the top.
    // We target the morph rect's top — currently `animTop.value` — and shift
    // the entire track by that delta so neighbors stay visually aligned.
    if (isBottomSheetOpen) {
      const shift = -sv.animTop.value;
      sv.trackTranslateY.value = withTiming(shift, OPEN_TIMING);
    } else {
      sv.trackTranslateY.value = withTiming(0, OPEN_TIMING);
    }
  }, [isBottomSheetOpen]);

  return {
    closeViewer,
    closeViewerInstant,
  };
};
